import { parseDateOfBirth } from '../date-of-birth';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccordionModule } from 'primeng/accordion';
import { ButtonDirective } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import { ClipboardService } from '@brighthub/utils';
import { CapitalizeFirstPipe } from '@brighthub/helpers';
import { ClientSessionService } from '@brighthub/uisession';
import { MemberMetadataHeaderService } from './member-metadata-header.service';
import { HouseholdMembersModalComponent } from './household-members-modal/household-members-modal.component';

/** Em dash fallback for null / unavailable detail fields (EDD — not the PRD double hyphen). */
const EM_DASH = '—';

export type ClientMetadataLink = { label: string; url: string };

/**
 * BrightNav member metadata header (MW-10562): collapsed primary bar (avatar initials, name,
 * employer, eligibility / VIP / DNC badges, client link icons) over an expandable details panel
 * (DOB/Age, masked SSN, address, phone, gender, language, employment status, salary, household
 * link, GUID copy + refresh). Header fields come from {@link MemberMetadataHeaderService}; client
 * link icons use metadata `links[]` when present, otherwise session `subjectData.links` (api-mobile
 * still supplies Intercom / Salesforce while the metadata API stubs `links: []`). Eligibility badge
 * is sourced from ui-session `subjectData.isEligible`, not the metadata API — see {@link isEligible}.
 *
 * States: hidden without a guid; primary-bar skeleton on cache miss (accordion not rendered, so
 * not expandable); neutral retry row when a fetch fails with nothing cached (toast via
 * {@link MemberMetadataHeaderService}); collapse resets on every guid change.
 */
@Component({
  selector: 'bn-client-metadata',
  imports: [
    CommonModule,
    AccordionModule,
    ButtonDirective,
    SkeletonModule,
    Tooltip,
    CapitalizeFirstPipe,
    HouseholdMembersModalComponent,
  ],
  templateUrl: './client-metadata.component.html',
  styleUrl: './client-metadata.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientMetadataComponent {
  private readonly _headerService = inject(MemberMetadataHeaderService);
  private readonly _clientSession = inject(ClientSessionService);
  private readonly _clipboardService = inject(ClipboardService);

  readonly emDash = EM_DASH;

  readonly activeGuid = this._headerService.activeGuid;
  readonly header = this._headerService.header;
  readonly loading = this._headerService.loading;
  readonly error = this._headerService.error;

  /**
   * Intercom / Salesforce (etc.) icon buttons for the primary bar. Prefer metadata `links` when the
   * API returns any; otherwise use session `subjectData.links` for the active guid.
   */
  readonly clientLinks = computed((): ClientMetadataLink[] => {
    const fromHeader = this.header()?.links?.filter((l) => !!l?.label) ?? [];
    if (fromHeader.length > 0) {
      return fromHeader;
    }
    const subject = this._clientSession.selectedSubjectData();
    const guid = this.activeGuid();
    if (!subject?.links?.length || !guid || subject.guid?.trim() !== guid) {
      return [];
    }
    return subject.links.filter((l) => !!l?.label);
  });

  /**
   * Eligibility badge — sourced from ui-session `subjectData.isEligible`, not the metadata API
   * (bug fix: the metadata API's `isEligible` is unreliable; session data is the source of truth).
   * `null` when session data isn't available for the active guid yet — badge renders neither state.
   */
  readonly isEligible = computed((): boolean | null => {
    const subject = this._clientSession.selectedSubjectData();
    const guid = this.activeGuid();
    if (!guid || !subject || subject.guid?.trim() !== guid) {
      return null;
    }
    return subject.isEligible;
  });

  /** Expanded accordion panel value (PrimeNG accordion model type); `null` = collapsed. Reset on every guid change. */
  readonly accordionValue = signal<string | number | string[] | number[] | null | undefined>(null);

  /** Skeleton renders only on a cache miss — a cached header displays immediately. */
  readonly showSkeleton = computed(() => this.loading() && !this.header());

  /**
   * Neutral retry row when a fetch failed and nothing is displayable. Error copy is shown via
   * toast from {@link MemberMetadataHeaderService}; a cached header stays visible without a banner.
   */
  readonly showRetry = computed(() => this.error() !== null && !this.header() && !this.loading());

  /** Avatar initials from first/last name (EDD OQ-16 default — may become employer logo). */
  readonly initials = computed(() => {
    const h = this.header();
    if (!h) {
      return '';
    }
    return `${h.firstName?.charAt(0) ?? ''}${h.lastName?.charAt(0) ?? ''}`.toUpperCase();
  });

  /** Single DOB / Age section value, e.g. `07/30/79 • 45` (age computed client-side). */
  readonly dobAgeDisplay = computed(() => {
    const dob = this.header()?.dateOfBirth;
    if (!dob) {
      return EM_DASH;
    }
    const parsed = parseDateOfBirth(dob);
    if (!parsed) return EM_DASH;
    const { year, month, day } = parsed;
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yy = String(year % 100).padStart(2, '0');
    const age = this._ageFrom(year, month, day);
    return `${mm}/${dd}/${yy} • ${age}`;
  });

  /** Masked SSN display (`••••3071`); digits-only last four for defense in depth (AC-12). */
  readonly ssnDisplay = computed(() => {
    const digits = (this.header()?.ssnLast4 ?? '').replace(/\D/g, '').slice(-4);
    return digits ? `••••${digits}` : EM_DASH;
  });

  readonly householdModalVisible = signal(false);

  constructor() {
    effect(() => {
      this.activeGuid();
      // Collapse on every navigation (AC-03); also closes any open household modal.
      this.accordionValue.set(null);
      this.householdModalVisible.set(false);
    });
  }

  /** Refresh-data icon in the expanded GUID footer — bypasses the cache TTL. */
  refresh(event: Event): void {
    event.stopPropagation();
    const guid = this.activeGuid();
    if (guid) {
      this._headerService.load(guid, { force: true });
    }
  }

  /** Opens a client link in a new tab (no Amplitude — EDD OQ-9). */
  openLink(event: Event, url: string): void {
    event.stopPropagation();
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  copyToClipboard(guid: string): void {
    this._clipboardService.copy(guid);
  }

  /** Household count click — lazy member fetch happens inside the modal (EDD OQ-8). */
  openHouseholdModal(): void {
    this.householdModalVisible.set(true);
  }

  private _ageFrom(year: number, month: number, day: number): number {
    const today = new Date();
    let age = today.getFullYear() - year;
    const hasHadBirthdayThisYear =
      today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);
    if (!hasHadBirthdayThisYear) {
      age--;
    }
    return age;
  }
}
